import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faDownload, faTrash, faChevronDown, faChevronRight,
  faFile, faFilePdf, faFileImage, faFileWord, faFileExcel, faSearch,
} from '@fortawesome/free-solid-svg-icons';
import api from '../../../Api';
import DocumentUploadModal from './DocumentUploadModal';
import ConfirmModal from './ConfirmModal';

const STEPS = [
  { key: 'ao', label: 'Avant-Vente (AO)', accent: '#6366f1' },
  { key: 'setup', label: 'Setup', accent: '#0ea5e9' },
  { key: 'terrain', label: 'Terrain', accent: '#10b981' },
  { key: 'reporting', label: 'Reporting', accent: '#f59e0b' },
  { key: 'closing', label: 'Closing', accent: '#8b5cf6' },
];

const ACCEPTED_TYPES = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.zip,.rar,.csv,.txt';

function fileIcon(mime) {
  if (!mime) return faFile;
  if (mime.includes('pdf')) return faFilePdf;
  if (mime.includes('image')) return faFileImage;
  if (mime.includes('word') || mime.includes('document')) return faFileWord;
  if (mime.includes('sheet') || mime.includes('excel')) return faFileExcel;
  return faFile;
}

function formatSize(bytes) {
  if (!bytes) return '0 KB';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(2) + ' MB';
}

const DocumentsTab = ({ projectId, currentStep, readOnly }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadModal, setUploadModal] = useState({ open: false, step: null });
  const [collapsed, setCollapsed] = useState({});
  const [search, setSearch] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, docId: null });
  const [errorModal, setErrorModal] = useState({ open: false, message: '' });

  // Initialize collapsed: all closed except current step
  useEffect(() => {
    const initial = {};
    STEPS.forEach(({ key }) => { initial[key] = key !== currentStep; });
    setCollapsed(initial);
  }, [currentStep]);

  const fetchDocs = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/projects/${projectId}/documents`);
      setDocuments(res.data.data || []);
    } catch (err) { console.error('Error loading documents:', err); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleUpload = async ({ step, label, file }) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('step', step); fd.append('label', label); fd.append('file', file);
      await api.post(`/projects/${projectId}/documents`, fd);
      setUploadModal({ open: false, step: null });
      fetchDocs();
    } catch (err) { setErrorModal({ open: true, message: err.response?.data?.message || 'Erreur lors de l\'upload' }); }
    finally { setUploading(false); }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await api.get(`/projects/${projectId}/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = doc.original_filename;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { setErrorModal({ open: true, message: 'Erreur lors du téléchargement' }); }
  };

  const confirmDelete = (docId) => {
    setDeleteConfirm({ open: true, docId });
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/projects/${projectId}/documents/${deleteConfirm.docId}`);
      setDeleteConfirm({ open: false, docId: null });
      fetchDocs();
    } catch { setDeleteConfirm({ open: false, docId: null }); setErrorModal({ open: true, message: 'Erreur lors de la suppression' }); }
  };

  const toggle = (step) => setCollapsed(prev => ({ ...prev, [step]: !prev[step] }));

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600" /></div>;

  const searchLower = search.toLowerCase().trim();
  const filteredDocs = searchLower
    ? documents.filter(d => d.label.toLowerCase().includes(searchLower) || d.original_filename.toLowerCase().includes(searchLower))
    : documents;
  const isSearching = searchLower.length > 0;

  return (
    <div className="docs-tab">
      {/* Header bar */}
      <div className="docs-header-bar">
        <div style={{ fontSize: '0.82rem', color: '#374151' }}>
          <strong>{documents.length}</strong> <span style={{ color: '#9ca3af' }}>document(s)</span>
        </div>
        <div className="docs-search-wrap">
          <div style={{ position: 'relative', flex: 1 }}>
            <FontAwesomeIcon icon={faSearch} size="xs" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher un fichier..."
              style={{ width: '100%', fontSize: '0.75rem', padding: '6px 10px 6px 28px', border: '1px solid #e5e7eb', borderRadius: 6, outline: 'none' }}
            />
          </div>
        </div>
        <div className="docs-hint" style={{ fontSize: '0.62rem', color: '#9ca3af' }}>
          PDF, Word, Excel, Images, ZIP · max 50 MB
        </div>
      </div>

      {/* Search results mode */}
      {isSearching ? (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ padding: '10px 16px', borderBottom: '1px solid #f3f4f6' }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151' }}>
              Résultats pour "{search}" — {filteredDocs.length} fichier(s)
            </span>
          </div>
          <div style={{ padding: filteredDocs.length > 0 ? '4px 16px 8px' : '0' }}>
            {filteredDocs.length === 0 ? (
              <div style={{ padding: '20px 0', textAlign: 'center', fontSize: '0.78rem', color: '#d1d5db' }}>Aucun résultat</div>
            ) : filteredDocs.map(doc => (
              <DocRow key={doc.id} doc={doc} onDownload={handleDownload} onDelete={confirmDelete} showStep readOnly={readOnly} />
            ))}
          </div>
        </div>
      ) : (
        /* Step-grouped mode */
        STEPS.map(({ key, label, accent }) => {
          const stepDocs = documents.filter(d => d.step === key);
          const isCollapsed = collapsed[key];

          return (
            <div key={key} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, marginBottom: 10, overflow: 'hidden' }}>
              <div
                onClick={() => toggle(key)}
                style={{
                  display: 'flex', alignItems: 'center', padding: '10px 16px', cursor: 'pointer',
                  borderBottom: isCollapsed ? 'none' : '1px solid #f3f4f6',
                }}
              >
                <FontAwesomeIcon icon={isCollapsed ? faChevronRight : faChevronDown} size="xs" style={{ color: '#9ca3af', marginRight: 10 }} />
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: accent, marginRight: 8 }} />
                <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', flex: 1 }}>{label}</span>
                <span style={{ fontSize: '0.65rem', fontWeight: 600, color: '#9ca3af', marginRight: 10 }}>{stepDocs.length}</span>
                {!readOnly && (
                  <button
                    onClick={e => { e.stopPropagation(); setUploadModal({ open: true, step: key }); }}
                    style={{ fontSize: '0.68rem', fontWeight: 600, padding: '3px 10px', borderRadius: 6, border: '1px solid #dc2626', background: '#fff', color: '#dc2626', cursor: 'pointer' }}
                  >
                    <FontAwesomeIcon icon={faPlus} size="xs" className="me-1" />Ajouter
                  </button>
                )}
              </div>

              {!isCollapsed && (
                <div style={{ padding: stepDocs.length > 0 ? '4px 16px 8px' : '0 16px' }}>
                  {stepDocs.length === 0 ? (
                    <div style={{ padding: '16px 0', textAlign: 'center', fontSize: '0.78rem', color: '#d1d5db', fontStyle: 'italic' }}>
                      Aucun document
                    </div>
                  ) : stepDocs.map(doc => (
                    <DocRow key={doc.id} doc={doc} onDownload={handleDownload} onDelete={confirmDelete} readOnly={readOnly} />
                  ))}
                </div>
              )}
            </div>
          );
        })
      )}

      <DocumentUploadModal
        isOpen={uploadModal.open}
        onClose={() => setUploadModal({ open: false, step: null })}
        onUpload={handleUpload}
        step={uploadModal.step}
        loading={uploading}
        acceptedTypes={ACCEPTED_TYPES}
      />

      <ConfirmModal
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, docId: null })}
        onConfirm={handleDelete}
        title="Supprimer ce document ?"
        message="Cette action est irréversible. Le fichier sera supprimé définitivement."
        variant="danger"
        confirmLabel="Supprimer"
      />

      <ConfirmModal
        isOpen={errorModal.open}
        onClose={() => setErrorModal({ open: false, message: '' })}
        title="Erreur"
        message={errorModal.message}
        variant="danger"
      />
      <style>{`
        .docs-header-bar {
          display: flex; align-items: center; justify-content: space-between; gap: 12px;
          background: #fff; border: 1px solid #e5e7eb; border-radius: 10px; padding: 10px 16px; margin-bottom: 16px;
          flex-wrap: wrap;
        }
        .docs-search-wrap { display: flex; align-items: center; gap: 8px; flex: 1; max-width: 280px; }
        .docs-row {
          display: flex; align-items: center; gap: 12px; padding: 10px 0;
          border-bottom: 1px solid #f9fafb;
        }
        @media (max-width: 768px) {
          .docs-header-bar { flex-direction: column; align-items: stretch; gap: 8px; padding: 10px 12px; }
          .docs-search-wrap { max-width: 100%; }
          .docs-hint { display: none; }
          .docs-row { flex-wrap: wrap; gap: 8px; }
        }
      `}</style>
    </div>
  );
};

const STEP_LABELS_SHORT = { ao: 'AO', setup: 'Setup', terrain: 'Terrain', reporting: 'Reporting', closing: 'Closing' };

const DocRow = ({ doc, onDownload, onDelete, showStep, readOnly }) => (
  <div className="docs-row">
    <div style={{ width: 32, height: 32, borderRadius: 8, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <FontAwesomeIcon icon={fileIcon(doc.mime_type)} size="sm" style={{ color: '#6b7280' }} />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {doc.label}
      </div>
      <div style={{ fontSize: '0.68rem', color: '#9ca3af' }}>
        {doc.original_filename} · {formatSize(doc.file_size)}
        {showStep && <> · <span style={{ fontWeight: 600 }}>{STEP_LABELS_SHORT[doc.step] || doc.step}</span></>}
        {' '}· {doc.uploaded_by?.name} · {new Date(doc.created_at).toLocaleDateString('fr-FR')}
      </div>
    </div>
    <button onClick={() => onDownload(doc)} title="Télécharger"
      style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', padding: 4 }}>
      <FontAwesomeIcon icon={faDownload} size="sm" />
    </button>
    {!readOnly && (
      <button onClick={() => onDelete(doc.id)} title="Supprimer"
        style={{ background: 'none', border: 'none', color: '#d1d5db', cursor: 'pointer', padding: 4 }}>
        <FontAwesomeIcon icon={faTrash} size="sm" />
      </button>
    )}
  </div>
);

export default DocumentsTab;
